import { Trans, t } from '@lingui/macro'
import { useLingui } from '@lingui/react'
import { Alert, Button, Pagination, Stack, Typography, useMediaQuery, useTheme } from '@mui/material'
import PersonAddAlt1Icon from '@mui/icons-material/PersonAddAlt1'

import DeleteConfirmationDialog from '@/components/ui/dialogs/DeleteConfirmationDialog'

import { useTrustedActors } from '../../model/useTrustedActors'
import { DesktopTable } from './DesktopTable'
import { MobileList } from './MobileList'
import { TrustedActorDialog } from './TrustedActorDialog'

export function TrustedActors() {
  useLingui()
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const {
    actors,
    actorToDelete,
    dialogOpen,
    editingActor,
    error,
    establishmentId,
    handleCloseDialog,
    handleConfirmRemove,
    handleOpenAdd,
    handleOpenEdit,
    handleSubmit,
    isAdding,
    isLoading,
    isRemoving,
    isUpdating,
    page,
    rangeEnd,
    rangeStart,
    setActorToDelete,
    setPage,
    totalCount,
    totalPages,
  } = useTrustedActors()

  if (error) {
    return (
      <Stack p={3}>
        <Alert severity="error">
          <Trans>Failed to load trusted actors</Trans>
        </Alert>
      </Stack>
    )
  }

  return (
    <Stack spacing={3} p={{ xs: 2, sm: 3 }}>
      {/* Header */}
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ xs: 'stretch', md: 'center' }} justifyContent="space-between">
        <Stack>
          <Typography variant="h5" sx={{ fontWeight: 700, letterSpacing: '-0.01em' }}>
            <Trans>Trusted actors</Trans>
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            <Trans>Manage the healthcare entities authorised to access your shared records.</Trans>
          </Typography>
        </Stack>
        <Button
          startIcon={<PersonAddAlt1Icon />}
          variant="contained"
          color="primary"
          onClick={handleOpenAdd}
          disabled={!establishmentId}
          sx={{ alignSelf: { xs: 'stretch', md: 'auto' }, textTransform: 'none', fontWeight: 600, px: 3 }}
        >
          <Trans>Add a trusted actor</Trans>
        </Button>
      </Stack>

      {/* Table (md+) / Card list (mobile) */}
      {isMobile ? (
        <MobileList actors={actors} isLoading={isLoading} onEdit={handleOpenEdit} onDelete={setActorToDelete} />
      ) : (
        <DesktopTable actors={actors} isLoading={isLoading} onEdit={handleOpenEdit} onDelete={setActorToDelete} />
      )}

      {/* Pagination footer */}
      {totalCount > 0 && (
        <Stack direction={{ xs: 'column', sm: 'row' }} alignItems="center" justifyContent="space-between" spacing={1.5} sx={{ px: 1 }}>
          <Typography variant="caption" color="text.secondary">
            <Trans>
              Showing {rangeStart}–{rangeEnd} of {totalCount} actors
            </Trans>
          </Typography>
          <Pagination count={totalPages} page={page} onChange={(_, value) => setPage(value)} color="primary" size="small" siblingCount={isMobile ? 0 : 1} />
        </Stack>
      )}

      <TrustedActorDialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        onSubmit={handleSubmit}
        submitting={isAdding || isUpdating}
        mode={editingActor ? 'edit' : 'create'}
        initialValues={editingActor ? { name: editingActor.name, description: editingActor.description ?? '' } : undefined}
      />

      <DeleteConfirmationDialog
        open={!!actorToDelete}
        isDeleting={isRemoving}
        title={t`Remove this trusted actor?`}
        message={actorToDelete ? t`"${actorToDelete.name}" will no longer be considered a trusted partner for INS identity exchange.` : ''}
        onClose={() => setActorToDelete(null)}
        onDelete={handleConfirmRemove}
      />
    </Stack>
  )
}
